import React, { useState, useEffect } from 'react';
import Card from './UI/Card';
import Button from './UI/Button';
import Slider from './UI/Slider';
import ProgressBar from './UI/ProgressBar';
import OnboardingOverlay from './UI/OnboardingOverlay';
import { useOnboarding } from '../hooks/useOnboarding';
import { useSimulator } from '../context/SimulatorContext';
import OldSchoolSlotMachine from './SlotMachine/OldSchoolSlotMachine';
import { useTranslation } from '../hooks/useTranslation';
import { LimitEventModal } from './UI/Modal';
import FeedbackScreen from './FeedbackScreen';
import Modal from './UI/Modal';
import LimitSetup from './LimitSetup';
import { sendAnalyticsEvent } from '../utils/analytics';

function NodeProgressBar({ current, total }) {
  const { t } = useTranslation();
  // Render a horizontal bar with total nodes, red for <= current, gray for > current
  return (
    <div className="flex items-center justify-center mb-4">
      {Array.from({ length: total }).map((_, i) => (
        <React.Fragment key={i}>
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs
              ${i < current ? 'bg-primary text-white' : 'bg-gray-300 text-gray-500'}`}
            aria-label={i + 1 === current ? t('simulation.current_week') : i + 1 < current ? t('simulation.completed_week') : t('simulation.upcoming_week')}
          >
            {i + 1}
          </div>
          {i < total - 1 && (
            <div className={`h-1 w-6 ${i < current - 1 ? 'bg-primary' : 'bg-gray-300'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function LimitRow({ label, values, maxes, unit = '', type = 'dwm', color, previewValues, illegalArray, illegal }) {
  const { t } = useTranslation();
  // type: 'dwm' for daily/weekly/monthly, 'single' for one value
  const formatValue = (value, unit) => {
    return unit === '€' ? `${unit}${value}` : `${value}${unit}`;
  };

  return (
    <div className="mb-1.5 min-h-[50px] flex flex-col justify-center">
      <div className="flex justify-between text-[11px] font-medium mb-0.5">
        <span>{label}</span>
        {type === 'dwm' ? null : <span className="text-[10px] font-medium text-gray-800">{formatValue(maxes[0], unit)}</span>}
      </div>
      {type === 'dwm' ? (
        <div className="flex gap-2">
          {maxes.map((max, i) => (
            <div key={i} className="flex-1">
              <div className="flex justify-between text-[10px] mb-0.5">
                <span className="text-gray-500">{t(['common.daily','common.weekly','common.monthly'][i])}</span>
                <span className="font-medium text-gray-800">{formatValue(max, unit)}</span>
              </div>
              <ProgressBar 
                value={Math.min(100, (values[i] / max) * 100)}
                previewValue={previewValues && previewValues[i] ? Math.min(100, (previewValues[i] / max) * 100) : undefined}
                illegal={illegalArray ? illegalArray[i] : illegal}
                color={color}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-0.5">
          <ProgressBar 
            value={Math.min(100, (values[0] / maxes[0]) * 100)}
            previewValue={previewValues && previewValues[0] ? Math.min(100, (previewValues[0] / maxes[0]) * 100) : undefined}
            illegal={illegalArray ? illegalArray[0] : illegal}
            color={color}
          />
        </div>
      )}
    </div>
  );
}

console.log('Simulation component render');

export default function Simulation() {
  const { t } = useTranslation();
  const { state, actions } = useSimulator();
  const { 
    isOnboardingActive, 
    currentStep, 
    hasCompletedOnboarding,
    nextStep, 
    previousStep,
    skipOnboarding, 
    completeOnboarding,
    resetOnboarding,
    startOnboarding
  } = useOnboarding();
  
  const [deposit, setDeposit] = useState(0);
  const [withdraw, setWithdraw] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [showPlayModal, setShowPlayModal] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(() => {
    const feedbackStatus = localStorage.getItem('feedbackStatus');
    return feedbackStatus === 'submitted';
  });
  const [showRestartModal, setShowRestartModal] = useState(false);

  // Refs for onboarding element positioning
  const cardContainerRef = React.useRef(null);
  const weeklyActivityRef = React.useRef(null);
  const limitsCardRef = React.useRef(null);
  const balanceStatsRef = React.useRef(null);
  const playButtonRef = React.useRef(null);
  const nextWeekButtonRef = React.useRef(null);

  // Onboarding steps configuration
  const onboardingSteps = [
    {
      content: t('onboarding.welcome.content'),
      tooltip: { top: '50%', left: '50%' }
    },
    {
      content: t('onboarding.weekly_activity.content'),
      elementRef: 'weeklyActivity',
      tooltip: { top: '50%', left: '50%' }
    },
    {
      content: t('onboarding.limits_overview.content'),
      elementRef: 'limitsCard',
      tooltip: { top: '30%', left: '50%' }
    },
    {
      content: t('onboarding.balance_stats.content'),
      elementRef: 'balanceStats',
      tooltip: { top: '50%', left: '50%' }
    },
    {
      content: t('onboarding.play_button.content'),
      elementRef: 'playButton',
      tooltip: { top: '50%', left: '50%' }
    },
    {
      content: t('onboarding.next_week.content'),
      elementRef: 'nextWeekButton',
      tooltip: { top: '50%', left: '50%' }
    }
  ];

  // Element refs for onboarding
  const elementRefs = {
    weeklyActivity: weeklyActivityRef,
    limitsCard: limitsCardRef,
    balanceStats: balanceStatsRef,
    playButton: playButtonRef,
    nextWeekButton: nextWeekButtonRef,
  };

  // Debug: log every render and modal state
  console.log('Simulation render', { showModal, modalContent });

  // Example: derive week and totalWeeks from state if available, else fallback
  const week = state.currentWeek || 1;
  const totalWeeks = 5; // Could be from config
  const limits = state.limits;
  const stats = {
    totalDeposited: state.totalDeposited,
    totalWithdrawn: state.totalWithdrawn,
    totalPlayTime: state.totalPlayTime,
    currentBalance: state.balance,
    weekLogin: [state.weeklyPlayTime || 0, 0, 0],
    weekNetResult: state.netResult,
  };

  // --- Limit logic ---
  // Deposit limits
  const netDepositLocked = state.limitStatus.netDepositReached;
  const depositLockedMonthly = state.limitStatus.depositReached.monthly;
  const depositLockedWeekly = state.limitStatus.depositReached.weekly;
  const depositLockedDaily = state.limitStatus.depositReached.daily;
  // Play limits
  const loginLockedMonthly = state.limitStatus.loginReached.monthly;
  const loginLockedWeekly = state.limitStatus.loginReached.weekly;
  const loginLockedDaily = state.limitStatus.loginReached.daily;
  // Automatic withdrawal
  const autoWithdrawalTriggered = state.limitStatus.withdrawalTriggered;

  // Button lock reasons
  let depositLockedReason = undefined;
  if (netDepositLocked || depositLockedMonthly || depositLockedWeekly || depositLockedDaily) depositLockedReason = 'lock';

  let playLockedReason = undefined;
  if (loginLockedMonthly || loginLockedWeekly || loginLockedDaily) playLockedReason = 'lock';

  // Check if current deposit amount would exceed limits
  const wouldExceedNetDeposit = (stats.totalDeposited - stats.totalWithdrawn + deposit) > limits.netDeposit;
  const wouldExceedDailyDeposit = (state.depositedToday + deposit) > limits.deposit.daily;
  const wouldExceedWeeklyDeposit = (state.depositedThisWeek + deposit) > limits.deposit.weekly;
  const wouldExceedMonthlyDeposit = (state.depositedThisMonth + deposit) > limits.deposit.monthly;

  // Determine which limit would be exceeded
  let depositExceedsLimit = false;
  if (wouldExceedNetDeposit) {
    depositExceedsLimit = true;
    depositLockedReason = 'lock';
  } else if (wouldExceedMonthlyDeposit) {
    depositExceedsLimit = true;
    depositLockedReason = 'lock';
  } else if (wouldExceedWeeklyDeposit) {
    depositExceedsLimit = true;
    depositLockedReason = 'lock';
  } else if (wouldExceedDailyDeposit) {
    depositExceedsLimit = true;
    depositLockedReason = 'lock';
  }

  // Deposit button disabled logic
  const depositDisabled =
    deposit === 0 ||
    netDepositLocked ||
    depositLockedMonthly ||
    depositLockedWeekly ||
    depositLockedDaily ||
    depositExceedsLimit;

  // Play button disabled logic
  const playDisabled =
    state.balance <= 0 ||
    loginLockedMonthly ||
    loginLockedWeekly ||
    loginLockedDaily;

  // Withdraw button disabled logic
  const withdrawDisabled =
    withdraw === 0 || withdraw > state.balance || state.balance === 0;

  // Track previous limit status to detect changes
  const prevLimitStatus = React.useRef(state.limitStatus);

  React.useEffect(() => {
    // Helper to show modal for a limit
    function showLimitModal(content) {
      setModalContent(content);
      setShowModal(true);
    }

    // Helper to check if a limit was newly reached
    function isNewlyReached(current, previous, path) {
      const currentValue = path.split('.').reduce((obj, key) => obj?.[key], current);
      const previousValue = path.split('.').reduce((obj, key) => obj?.[key], previous);
      return currentValue && !previousValue;
    }

    // Net deposit
    if (isNewlyReached(state.limitStatus, prevLimitStatus.current, 'netDepositReached')) {
      showLimitModal({
        title: t('alerts.net_deposit_limit_reached.title'),
        message: t('alerts.net_deposit_limit_reached.message'),
        iconType: 'lock',
      });
    }
    // Deposit monthly
    else if (isNewlyReached(state.limitStatus, prevLimitStatus.current, 'depositReached.monthly')) {
      showLimitModal({
        title: t('alerts.deposit_limit_reached.title'),
        message: t('alerts.deposit_limit_reached.monthly'),
        iconType: 'lock',
      });
    }
    // Deposit weekly
    else if (isNewlyReached(state.limitStatus, prevLimitStatus.current, 'depositReached.weekly')) {
      showLimitModal({
        title: t('alerts.deposit_limit_reached.title'),
        message: t('alerts.deposit_limit_reached.weekly'),
        iconType: 'lock',
      });
    }
    // Deposit daily
    else if (isNewlyReached(state.limitStatus, prevLimitStatus.current, 'depositReached.daily')) {
      showLimitModal({
        title: t('alerts.deposit_limit_reached.title'),
        message: t('alerts.deposit_limit_reached.daily'),
        iconType: 'lock',
      });
    }
    // Login monthly
    else if (isNewlyReached(state.limitStatus, prevLimitStatus.current, 'loginReached.monthly')) {
      showLimitModal({
        title: t('alerts.login_limit_reached.title'),
        message: t('alerts.login_limit_reached.monthly'),
        iconType: 'lock',
      });
    }
    // Login weekly
    else if (isNewlyReached(state.limitStatus, prevLimitStatus.current, 'loginReached.weekly')) {
      showLimitModal({
        title: t('alerts.login_limit_reached.title'),
        message: t('alerts.login_limit_reached.weekly'),
        iconType: 'lock',
      });
    }
    // Login daily
    else if (isNewlyReached(state.limitStatus, prevLimitStatus.current, 'loginReached.daily')) {
      showLimitModal({
        title: t('alerts.login_limit_reached.title'),
        message: t('alerts.login_limit_reached.daily'),
        iconType: 'lock',
      });
    }
    // Automatic withdrawal
    else if (isNewlyReached(state.limitStatus, prevLimitStatus.current, 'withdrawalTriggered')) {
      showLimitModal({
        title: t('limits.withdrawal'),
        message: t('limits.withdrawal_detail.desc'),
        iconType: undefined,
      });
    }

    // Update previous limit status
    prevLimitStatus.current = JSON.parse(JSON.stringify(state.limitStatus));
  }, [state.limitStatus, t]);

  // --- Modal trigger handlers ---
  function handleDepositClick() {
    if (depositDisabled && deposit !== 0) {
      // Show modal for limit
      let title = '';
      let message = '';
      let iconType = depositLockedReason;
      
      if (wouldExceedNetDeposit) {
        title = t('alerts.net_deposit_limit_reached.title');
        message = t('alerts.net_deposit_limit_reached.message');
      } else if (wouldExceedMonthlyDeposit) {
        title = t('alerts.deposit_limit_reached.title');
        message = t('alerts.deposit_limit_reached.monthly');
      } else if (wouldExceedWeeklyDeposit) {
        title = t('alerts.deposit_limit_reached.title');
        message = t('alerts.deposit_limit_reached.weekly');
      } else if (wouldExceedDailyDeposit) {
        title = t('alerts.deposit_limit_reached.title');
        message = t('alerts.deposit_limit_reached.daily');
      } else if (netDepositLocked) {
        title = t('alerts.net_deposit_limit_reached.title');
        message = t('alerts.net_deposit_limit_reached.message');
      } else if (depositLockedMonthly) {
        title = t('alerts.deposit_limit_reached.title');
        message = t('alerts.deposit_limit_reached.monthly');
      } else if (depositLockedWeekly) {
        title = t('alerts.deposit_limit_reached.title');
        message = t('alerts.deposit_limit_reached.weekly');
      } else if (depositLockedDaily) {
        title = t('alerts.deposit_limit_reached.title');
        message = t('alerts.deposit_limit_reached.daily');
      }
      
      setModalContent({ title, message, iconType });
      setShowModal(true);
      return;
    }
    actions.deposit(deposit);
    setDeposit(0);
  }

  function handlePlayClick() {
    if (playDisabled) {
      let title = '';
      let message = '';
      let iconType = playLockedReason;
      
      if (loginLockedMonthly) {
        title = t('alerts.login_limit_reached.title');
        message = t('alerts.login_limit_reached.monthly');
      } else if (loginLockedWeekly) {
        title = t('alerts.login_limit_reached.title');
        message = t('alerts.login_limit_reached.weekly');
      } else if (loginLockedDaily) {
        title = t('alerts.login_limit_reached.title');
        message = t('alerts.login_limit_reached.daily');
      }
      
      setModalContent({ title, message, iconType });
      setShowModal(true);
      return;
    }
    setShowPlayModal(true);
  }

  const handleNextWeek = () => {
    actions.nextWeek();
    setDeposit(0);
    setWithdraw(0);
  };

  // Log every change to showModal and modalContent
  React.useEffect(() => {
    console.log('showModal changed:', showModal, modalContent);
  }, [showModal, modalContent]);

  // Log when Simulation unmounts
  React.useEffect(() => {
    return () => {
      console.log('Simulation unmounted');
    };
  }, []);

  const handleFeedbackSubmit = (feedback) => {
    console.log('Feedback submitted:', feedback);
    
    // Send analytics event with feedback data
    sendAnalyticsEvent('feedback_submitted', {
      overall_rating: feedback.overallRating,
      ease_of_use_rating: feedback.specificRatings.easeOfUse,
      educational_rating: feedback.specificRatings.educational,
      engaging_rating: feedback.specificRatings.engaging,
      limit_awareness_rating: feedback.specificRatings.limitAwareness,
      realistic_rating: feedback.specificRatings.realistic,
      average_rating: Object.values(feedback.specificRatings).reduce((sum, rating) => sum + rating, 0) / Object.keys(feedback.specificRatings).length
    });
    
    if (!hasSubmittedFeedback) {
      // Only send/store feedback the first time
      // Here you would typically send the feedback to your backend
      localStorage.setItem('feedbackStatus', 'submitted');
      setHasSubmittedFeedback(true);
    }
    setShowFeedback(false);
  };

  const handleFeedbackSkip = () => {
    // Send analytics event for skipped feedback
    sendAnalyticsEvent('feedback_skipped');
    
    // Track that feedback was skipped (not submitted)
    localStorage.setItem('feedbackStatus', 'skipped');
    
    setShowFeedback(false);
  };

  // Handler for summary button
  const handleStartNewClick = () => {
    setShowRestartModal(true);
  };

  // Handler for modal options
  const handleRestartWithNewLimits = () => {
    setShowRestartModal(false);
    actions.reset({ withNewLimits: true });
  };
  const handleRestartWithSameLimits = () => {
    setShowRestartModal(false);
    actions.reset({ withNewLimits: false });
  };

  // --- Preview logic for deposit/withdrawal sliders ---
  // Deposit preview values for each limit
  const depositPreviewValues = [state.depositedToday + deposit, state.depositedThisWeek + deposit, state.depositedThisMonth + deposit];
  const depositLimits = [limits.deposit.daily, limits.deposit.weekly, limits.deposit.monthly];
  const depositIllegalArray = depositPreviewValues.map((val, i) => val > depositLimits[i]);
  const depositIllegal = depositIllegalArray.some(Boolean) || (deposit > 0 && (state.totalDeposited + deposit - state.totalWithdrawn) > limits.netDeposit);

  // Net deposit preview logic
  const netDepositCurrent = state.totalDeposited - state.totalWithdrawn;
  const netDepositPreview = state.totalDeposited + deposit - (state.totalWithdrawn + withdraw);
  const netDepositIllegal = netDepositPreview > limits.netDeposit;

  // Balance preview - only illegal if withdrawal exceeds available balance
  const previewBalance = state.balance + deposit - withdraw;
  const balanceIllegal = withdraw > 0 && withdraw > state.balance;

  // Withdrawal limit preview logic
  const withdrawalLimit = limits.withdrawal.maxBalance;
  const withdrawalPreviewValue = previewBalance > withdrawalLimit ? previewBalance : previewBalance;

  // Handle deposit slider change - reset withdraw to 0
  const handleDepositChange = (value) => {
    setDeposit(Number(value));
    if (Number(value) > 0) {
      setWithdraw(0);
    }
  };

  // Handle withdraw slider change - reset deposit to 0
  const handleWithdrawChange = (value) => {
    setWithdraw(Number(value));
    if (Number(value) > 0) {
      setDeposit(0);
    }
  };

  if (showFeedback) {
    return <FeedbackScreen onSubmit={handleFeedbackSubmit} onSkip={handleFeedbackSkip} />;
  }

  if (state.isComplete) {
    // Calculate totals
    const totals = state.weekHistory.reduce((acc, week) => {
      acc.deposited += week.deposited;
      acc.withdrawn += week.withdrawn;
      acc.playTime += week.playTime;
      acc.netResult += week.netResult;
      return acc;
    }, { deposited: 0, withdrawn: 0, playTime: 0, netResult: 0 });

    return (
      <Card className="p-3 sm:p-6">
        <div className="space-y-4">
          {/* Main Title */}
          <div className="text-center">
            <h1 className="text-2xl font-bold text-heading text-hc-black mb-2">{t('simulation.complete.title')}</h1>
            <p className="text-gray-600 text-body">{t('simulation.complete.subtitle')}</p>
          </div>

          {/* Week History Cards - Always 2 columns */}
          <div className="grid grid-cols-2 gap-3">
            {state.weekHistory.map((weekData, index) => (
              <Card key={index} className="p-3">
                <div className="flex flex-col h-full">
                  {/* Week Header */}
                  <div className="flex items-center mb-2">
                    <h3 className="text-lg font-bold text-heading text-hc-black">{t('simulation.week', { number: weekData.week })}</h3>
                  </div>
                  
                  {/* Week Data */}
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('common.deposit')}</span>
                      <span className="font-medium text-hc-black">€{weekData.deposited}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('common.withdraw')}</span>
                      <span className="font-medium text-hc-black">€{weekData.withdrawn}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('simulation.activity.login_time')}</span>
                      <span className="font-medium text-hc-black">{weekData.playTime}h</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('simulation.activity.net_result')}</span>
                      <span className={`font-medium ${weekData.netResult > 0 ? 'text-green-600' : weekData.netResult < 0 ? 'text-red-600' : 'text-hc-black'}`}>
                        {weekData.netResult > 0 ? '+' : ''}€{weekData.netResult}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                      <span className="text-xs text-gray-500">{t('simulation.complete.week_details.final_balance', { amount: 0 }).split(':')[0]}</span>
                      <span className="text-xs font-medium text-hc-black">€{weekData.finalBalance}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
            
            {/* Monthly Summary Card - Same size as week cards */}
            <Card className="p-3">
              <div className="flex flex-col h-full">
                {/* Monthly Header */}
                <div className="flex items-center mb-2">
                  <h3 className="text-lg font-bold text-heading text-hc-black">{t('simulation.complete.monthly_summary')}</h3>
                </div>
                
                {/* Monthly Data */}
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t('simulation.total_deposited')}</span>
                    <span className="font-medium text-hc-black">€{totals.deposited}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t('simulation.total_withdrawn')}</span>
                    <span className="font-medium text-hc-black">€{totals.withdrawn}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t('simulation.total_play_time')}</span>
                    <span className="font-medium text-hc-black">{totals.playTime}h</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t('simulation.activity.net_result')}</span>
                    <span className={`font-medium ${totals.netResult > 0 ? 'text-green-600' : totals.netResult < 0 ? 'text-red-600' : 'text-hc-black'}`}>
                      {totals.netResult > 0 ? '+' : ''}€{totals.netResult}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                    <span className="text-xs text-gray-500">{t('simulation.complete.week_details.final_balance', { amount: 0 }).split(':')[0]}</span>
                    <span className="text-xs font-medium text-hc-black">€{state.weekHistory[state.weekHistory.length - 1]?.finalBalance || 0}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Action Buttons - Side by side */}
          <div className="flex gap-3">
            <Button
              fullWidth
              onClick={handleStartNewClick}
              variant="secondary"
            >
              {t('simulation.complete.start_new')}
            </Button>
            <Button
              fullWidth
              onClick={() => setShowFeedback(true)}
              variant="primary"
              disabled={hasSubmittedFeedback}
            >
              {hasSubmittedFeedback 
                ? t('simulation.complete.feedback_submitted', 'Feedback submitted') 
                : t('simulation.complete.rate_experience', 'Rate your experience')
              }
            </Button>
          </div>

          {/* Restart Modal */}
          <Modal open={showRestartModal} onClose={() => setShowRestartModal(false)}>
            <div className="flex flex-col items-center gap-4">
              <div className="text-2xl font-bold mb-2 text-heading text-hc-black">{t('simulation.complete.restart_modal_title', 'Start a new simulation')}</div>
              <div className="text-gray-700 mb-4 text-center">{t('simulation.complete.restart_modal_desc', 'Would you like to set new limits or use the same limits?')}</div>
              <div className="flex flex-col gap-2 w-full">
                <Button fullWidth variant="primary" onClick={handleRestartWithNewLimits}>{t('simulation.complete.restart_new_limits', 'Start with new limits')}</Button>
                <Button fullWidth variant="secondary" onClick={handleRestartWithSameLimits}>{t('simulation.complete.restart_same_limits', 'Start with same limits')}</Button>
              </div>
            </div>
          </Modal>
        </div>
      </Card>
    );
  }

  if (state.showLimitSetup) {
    return <LimitSetup />;
  }

  console.log('Simulation: Rendering main simulation because showLimitSetup =', state.showLimitSetup);
  return (
    <Card ref={cardContainerRef} className="p-3 sm:p-6 relative">
      {/* Help Button */}
      {hasCompletedOnboarding && (
        <button
          onClick={() => {
            // Reset onboarding and start it again
            resetOnboarding();
            startOnboarding();
          }}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
          title={t('common.help', 'Help')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      )}

      {/* Week Heading */}
      <div className="text-2xl font-bold text-center mb-2 text-heading text-hc-black">{t('simulation.week', { number: week })}</div>
      {/* Node Progress Bar */}
      <NodeProgressBar current={week} total={totalWeeks} />

      {/* Weekly Activity Controls */}
      <div ref={weeklyActivityRef} className="bg-gray-70 rounded-xl p-4 mb-4 shadow-soft">
        <div className="mb-3 font-semibold text-left text-lg text-medium">{t('simulation.activity.title')}</div>
        <div className="space-y-2">
          {/* Deposit Row */}
          <div className="flex flex-row items-stretch gap-2">
            <div className="flex-1 flex items-center">
              <Slider
                min={0}
                max={300}
                step={10}
                value={deposit}
                onChange={e => handleDepositChange(e.target.value)}
                label={t('simulation.activity.deposit_label')}
                showValue={false}
              />
            </div>
            <Button
              variant="secondary"
              className="w-32 text-center truncate text-sm"
              onClick={handleDepositClick}
              fullWidth={false}
              disabled={depositDisabled}
              lockedReason={depositLockedReason}
            >
              {t('simulation.activity.deposit_button', { amount: deposit })}
            </Button>
          </div>
          {/* Withdraw Row */}
          <div className="flex flex-row items-stretch gap-2">
            <div className="flex-1 flex items-center">
              <Slider
                min={0}
                max={300}
                step={10}
                value={withdraw}
                onChange={e => handleWithdrawChange(e.target.value)}
                label={t('simulation.activity.withdraw_label')}
                showValue={false}
              />
            </div>
            <Button
              variant="secondary"
              className="w-32 text-center truncate text-sm"
              onClick={() => {
                actions.withdraw(withdraw);
                setWithdraw(0);
              }}
              fullWidth={false}
              disabled={withdrawDisabled}
            >
              {t('simulation.activity.withdraw_button', { amount: withdraw })}
            </Button>
          </div>
          {/* Play Row: Login Time + Net Result (left), Play button (right) */}
          <div className="flex flex-row items-stretch gap-2">
            <div className="flex-1 flex flex-row justify-between">
              <div className="flex-1 flex flex-col items-center px-2">
                <div className="text-[10px] text-gray-500 mb-0.5">{t('simulation.activity.login_time')}</div>
                <div className="text-[12px] font-semibold text-center">{stats.weekLogin[0]}h</div>
              </div>
              <div className="flex-1 flex flex-col items-center px-2">
                <div className="text-[10px] text-gray-500 mb-0.5">{t('simulation.activity.net_result')}</div>
                <div className={`text-[12px] font-semibold text-center ${stats.weekNetResult > 0 ? 'text-green-600' : stats.weekNetResult < 0 ? 'text-red-600' : 'text-gray-700'}`}>{stats.weekNetResult > 0 ? '+' : ''}€{stats.weekNetResult}</div>
              </div>
            </div>
            <Button
              ref={playButtonRef}
              variant="primary"
              onClick={handlePlayClick}
              className="w-32 text-center truncate text-sm"
              fullWidth={false}
              disabled={playDisabled}
              lockedReason={playLockedReason}
            >
              {t('simulation.activity.play_button')}
            </Button>
          </div>
        </div>
      </div>

      {/* Limits Card */}
      <Card ref={limitsCardRef} className="w-full mb-4">
        <div className="space-y-1">
          {/* Net Deposit Limit Row with custom right label */}
          <div className="mb-1.5 min-h-[50px] flex flex-col justify-center">
            <div className="flex justify-between text-[11px] font-medium mb-0.5">
              <span>{t('limits.net_deposit')}</span>
              <span>
                €{limits.netDeposit}
                {(stats.totalDeposited - stats.totalWithdrawn) < 0 && (
                  <span className="text-green-600"> (+€{Math.abs(stats.totalDeposited - stats.totalWithdrawn)})</span>
                )}
              </span>
            </div>
            <div className="mt-0.5">
              <ProgressBar 
                value={Math.max(0, Math.min(100, (netDepositCurrent / limits.netDeposit) * 100))}
                previewValue={(deposit > 0 || withdraw > 0) ? Math.max(0, Math.min(100, (netDepositPreview / limits.netDeposit) * 100)) : undefined}
                illegal={netDepositIllegal}
                color="#ff616d"
              />
            </div>
          </div>
          <LimitRow 
            label={t('limits.deposit')} 
            values={[state.depositedToday, state.depositedThisWeek, state.depositedThisMonth]} 
            maxes={[limits.deposit.daily, limits.deposit.weekly, limits.deposit.monthly]} 
            unit="€" 
            color="#ff616d"
            previewValues={depositPreviewValues}
            illegalArray={depositIllegalArray}
            illegal={depositIllegal}
          />
          <LimitRow 
            label={t('limits.login_time')} 
            values={[state.playTimeToday, state.weeklyPlayTime, state.totalPlayTime]}
            maxes={[limits.login.daily, limits.login.weekly, limits.login.monthly]} 
            unit="h" 
            color="#ff616d"
          />
          <LimitRow 
            label={t('limits.withdrawal')} 
            values={[stats.currentBalance]} 
            maxes={[limits.withdrawal.maxBalance]} 
            unit="€" 
            type="single" 
            color="#ff616d"
            previewValues={[withdrawalPreviewValue]}
            illegalArray={[false]}
            illegal={false}
          />
        </div>
      </Card>

      {/* Balance and Statistics Cards (always side by side) */}
      <div ref={balanceStatsRef} className="grid grid-cols-2 gap-2 mb-4 min-h-[90px]">
        {/* Balance Card */}
        <div className={`rounded-xl shadow-soft p-2 flex flex-col items-center justify-center min-h-[60px] ${balanceIllegal ? 'border-2 border-red-500 bg-red-50 animate-pulse' : ''}`}>
          <div className="font-semibold text-xs mb-0.5 opacity-90">{t('simulation.balance')}</div>
          <div className="text-2xl font-bold">
            €{stats.currentBalance}
            {previewBalance !== stats.currentBalance && (
              <span className={`ml-2 text-lg font-semibold ${balanceIllegal ? 'text-red-500 animate-pulse' : ''}`} style={{ color: balanceIllegal ? undefined : '#ECACB0' }}>→ €{previewBalance}</span>
            )}
          </div>
        </div>
        {/* Statistics Card */}
        <Card>
          <div className="text-[10px] space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-500">{t('simulation.total_play_time')}</span>
              <span className="font-medium">{stats.totalPlayTime}h</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t('simulation.total_deposited')}</span>
              <span className="font-medium">€{stats.totalDeposited}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t('simulation.total_withdrawn')}</span>
              <span className="font-medium">€{stats.totalWithdrawn}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t('simulation.activity.net_result')}</span>
              <span className="font-medium">{(stats.currentBalance - stats.totalDeposited + stats.totalWithdrawn) > 0 ? '+' : ''}€{stats.currentBalance - stats.totalDeposited + stats.totalWithdrawn}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Next Week Button */}
      <Button
        ref={nextWeekButtonRef}
        fullWidth
        onClick={handleNextWeek}
        disabled={week > totalWeeks}
        className="mt-2"
        variant="primary"
      >
        {week === totalWeeks ? t('simulation.complete.view_results', 'View Results') : t('simulation.next_week')}
      </Button>

      {/* Onboarding Overlay */}
      <OnboardingOverlay
        isActive={isOnboardingActive && !showPlayModal}
        currentStep={currentStep}
        onNext={nextStep}
        onPrevious={previousStep}
        onSkip={skipOnboarding}
        onComplete={completeOnboarding}
        steps={onboardingSteps}
        elementRefs={elementRefs}
        containerRef={cardContainerRef}
      />

      {/* Slot Machine Modal */}
      {showPlayModal && (
        <OldSchoolSlotMachine
          onClose={() => setShowPlayModal(false)}
          containerRef={cardContainerRef}
        />
      )}

      <LimitEventModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={modalContent?.title}
        message={modalContent?.message}
        iconType={modalContent?.iconType}
      >
        <div className="flex items-center justify-center gap-2 mt-2">
          <span className="inline-flex items-center"><svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="8" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg> = {t('limits.reached')}</span>
        </div>
      </LimitEventModal>
    </Card>
  );
} 